export const getContentApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || error?.message || fallback;

  if (Array.isArray(data?.errors) && data.errors.length) {
    return `${message}: ${data.errors.join(", ")}`;
  }

  if (typeof data?.error === "string" && data.error) {
    return data.error;
  }

  return message;
};

export const normalizeContents = (data) => {
  if (Array.isArray(data)) return data.filter(Boolean);
  if (Array.isArray(data?.data)) return data.data.filter(Boolean);
  if (data && typeof data === "object") return [data];
  return [];
};

export const normalizeContent = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data[0] || null;
  if (data?.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data;
};

export const formatContentDate = (dateValue) => {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

export const shortenId = (id, length = 8) => {
  const value = String(id || "");
  if (!value) return "-";
  if (value.length <= length) return value;
  return `${value.slice(0, length)}…`;
};

export const getContentPageCount = (content) => {
  const pages = content?.pages;
  return Array.isArray(pages) ? pages.length : 0;
};

export const getContentTopLevelKeys = (content) => {
  if (!content || typeof content !== "object" || Array.isArray(content)) {
    return [];
  }
  return Object.keys(content);
};

export const getThemeColors = (content) => {
  const theme = content?.theme;
  if (!theme || typeof theme !== "object") return null;
  return {
    primaryColor: theme.primaryColor || "",
    secondaryColor: theme.secondaryColor || "",
    textColor: theme.textColor || "",
    buttonHoverColor: theme.buttonHoverColor || "",
  };
};

export const deepClone = (value) => {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
};

export const getAtPath = (root, path) => {
  if (!path?.length) return root;
  let cursor = root;
  for (const key of path) {
    if (cursor == null) return undefined;
    cursor = cursor[key];
  }
  return cursor;
};

export const setAtPath = (root, path, value) => {
  if (!path?.length) return value;

  const cloneNode = (node, depth) => {
    if (depth >= path.length) return value;

    const key = path[depth];
    const isNextIndex = typeof path[depth + 1] === "number";

    if (Array.isArray(node)) {
      const next = node.map((item, index) =>
        index === key ? cloneNode(item, depth + 1) : item
      );
      if (key === next.length && depth === path.length - 1) {
        next.push(value);
        return next;
      }
      if (!(key in next) || next[key] === undefined) {
        next[key] = depth === path.length - 1
          ? value
          : cloneNode(isNextIndex ? [] : {}, depth + 1);
      }
      return next;
    }

    const source =
      node && typeof node === "object" && !Array.isArray(node) ? node : {};
    const child =
      key in source && source[key] !== undefined
        ? source[key]
        : isNextIndex
          ? []
          : {};

    return {
      ...source,
      [key]:
        depth === path.length - 1 ? value : cloneNode(child, depth + 1),
    };
  };

  return cloneNode(root && typeof root === "object" ? root : {}, 0);
};

const IMAGE_KEY_PATTERN =
  /^(image|bgImg|bgImage|frameImage|trackImage|headerPatternImage|footerFlagImage|leftFlagImage|rightFlagImage)$/i;

export const isImageFieldKey = (key) => IMAGE_KEY_PATTERN.test(String(key || ""));

export const looksLikeImagePath = (value) => {
  if (typeof value !== "string" || !value.trim()) return false;
  const v = value.trim();
  if (/\/uploads\//i.test(v)) return true;
  if (/\.(jpe?g|png|gif|webp|avif|svg)(\?|#|$)/i.test(v)) return true;
  return false;
};

export const shouldUseMediaForStringArray = (fieldKey, items) => {
  const key = String(fieldKey || "").toLowerCase();
  if (key === "checklist") return false;
  if (key === "items" || key === "images" || key === "partners") {
    const sample = (items || []).find(
      (item) => typeof item === "string" && item.trim()
    );
    if (!sample) return key === "images" || key === "partners";
    return looksLikeImagePath(sample);
  }
  return (items || []).some(
    (item) => typeof item === "string" && looksLikeImagePath(item)
  );
};

export const humanizeKey = (key) => {
  const value = String(key || "");
  if (!value) return "";
  return value
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatPageLabel = (pageId) => {
  const labels = {
    home: "Home",
    siteHeader: "Site Header",
    siteFooter: "Site Footer",
    about: "About",
    aboutEvent: "About Event",
    players: "Players",
    rankings: "Rankings",
    previousRallies: "Previous Rallies",
    rallyMap: "Rally Map",
    partners: "Partners",
  };
  return labels[pageId] || humanizeKey(pageId);
};

export const THEME_FOCUS_ID = "theme";

/** Flatten API content documents into UI list rows (theme + each page). */
export const flattenContentToListItems = (documents = []) => {
  const items = [];

  (Array.isArray(documents) ? documents : []).forEach((document) => {
    const contentId = document?._id || document?.id;
    if (!contentId) return;

    const content =
      document?.content && typeof document.content === "object"
        ? document.content
        : {};
    const pages = Array.isArray(content.pages) ? content.pages : [];

    items.push({
      kind: "theme",
      key: `${contentId}-${THEME_FOCUS_ID}`,
      contentId,
      focusId: THEME_FOCUS_ID,
      label: "Theme",
      sectionCount: null,
      document,
      content,
      created_at: document.created_at,
      updated_at: document.updated_at,
    });

    pages.forEach((page, index) => {
      const pageId = page?.id || `page-${index}`;
      items.push({
        kind: "page",
        key: `${contentId}-${pageId}`,
        contentId,
        focusId: pageId,
        pageId,
        pageIndex: index,
        label: formatPageLabel(pageId),
        sectionCount: Array.isArray(page?.sections) ? page.sections.length : 0,
        document,
        content,
        page,
        created_at: document.created_at,
        updated_at: document.updated_at,
      });
    });
  });

  return items;
};

export const createEmptyFromSample = (sample) => {
  if (sample === null || sample === undefined) return "";
  if (typeof sample === "string") return "";
  if (typeof sample === "number") return 0;
  if (typeof sample === "boolean") return false;
  if (Array.isArray(sample)) return [];
  if (typeof sample === "object") {
    const empty = {};
    Object.keys(sample).forEach((key) => {
      if (key === "id") {
        empty[key] =
          typeof sample[key] === "number" ? Date.now() % 100000 : "";
      } else {
        empty[key] = createEmptyFromSample(sample[key]);
      }
    });
    return empty;
  }
  return "";
};

export const validateContentObject = (content) => {
  if (!content || typeof content !== "object" || Array.isArray(content)) {
    return { content: "Content must be a non-empty object." };
  }
  if (!Object.keys(content).length) {
    return { content: "Content must be a non-empty object." };
  }
  return {};
};

export const buildContentUpdatePayload = (contentObject) => ({
  content: contentObject,
});

export const THEME_COLOR_KEYS = [
  "primaryColor",
  "secondaryColor",
  "textColor",
  "buttonHoverColor",
];
